/**
 * Sidebar para dispositivos móviles
 * Se muestra como un drawer deslizable
 */
import { useState, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider,
  Chip,
  alpha,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Home } from "lucide-react";
import { topLevelItems, navigationGroups } from "../../config/navigation";
import { usePermissions } from "../../contexts/permissions-context";
import logo from "../../assets/logo-nemesis.svg";

interface MobileSidebarProps {
  onClose: () => void;
}

export function MobileSidebar({ onClose }: MobileSidebarProps) {
  const location = useLocation();
  const pathname = location.pathname;
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const { canAccess } = usePermissions();

  const filteredTopItems = useMemo(
    () => topLevelItems.filter((item) => canAccess(item.href)),
    [canAccess],
  );

  const filteredGroups = useMemo(
    () =>
      navigationGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => canAccess(item.href)),
        }))
        .filter((group) => group.items.length > 0),
    [canAccess],
  );

  const toggleGroup = (groupTitle: string) => {
    setOpenGroups((prev) => ({
      ...prev,
      [groupTitle]: !prev[groupTitle],
    }));
  };

  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      {/* Logo Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          px: 2,
          py: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <img
          src={logo}
          alt="Nemesis"
          style={{
            width: 32,
            height: 32,
            objectFit: "contain",
          }}
        />
        <Typography variant="h6" fontWeight="bold" color="primary">
          Nemesis
        </Typography>
      </Box>

      {/* Navigation List */}
      <Box sx={{ flex: 1, overflowY: "auto", py: 1 }}>
        <List disablePadding>
          {/* Home */}
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/"
              onClick={onClose}
              selected={pathname === "/"}
              sx={{
                mx: 1,
                borderRadius: 1,
                mb: 0.5,
                "&.Mui-selected": {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                  "&:hover": {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18),
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Home size={20} />
              </ListItemIcon>
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{
                  fontSize: "0.9rem",
                  fontWeight: pathname === "/" ? 600 : 400,
                }}
              />
            </ListItemButton>
          </ListItem>

          <Divider sx={{ my: 1 }} />

          {/* Top Level Items */}
          {filteredTopItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            const isDisabled = item.disabled || item.comingSoon;

            return (
              <ListItem key={item.href} disablePadding>
                <ListItemButton
                  component={isDisabled ? "div" : Link}
                  to={isDisabled ? undefined : item.href}
                  onClick={isDisabled ? undefined : onClose}
                  disabled={isDisabled}
                  selected={active}
                  sx={{
                    mx: 1,
                    borderRadius: 1,
                    mb: 0.5,
                    opacity: isDisabled ? 0.5 : 1,
                    "&.Mui-selected": {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                      "&:hover": {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.18),
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, "& svg": { width: 20, height: 20 } }}>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontSize: "0.9rem",
                      fontWeight: active ? 600 : 400,
                    }}
                  />
                  {item.badge && (
                    <Chip
                      label={item.badge}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: item.comingSoon ? "warning.light" : "primary.light",
                        color: item.comingSoon ? "warning.dark" : "primary.dark",
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}

          {/* Navigation Groups */}
          {filteredGroups.map((group) => {
            const GroupIcon = group.icon;
            const isOpen = openGroups[group.title];
            const hasActiveItem = group.items.some((item) => isActive(item.href));

            return (
              <Box key={group.title}>
                <Divider sx={{ my: 1 }} />
                <ListItem disablePadding>
                  <ListItemButton
                    onClick={() => toggleGroup(group.title)}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      bgcolor: hasActiveItem
                        ? (theme) => alpha(theme.palette.primary.main, 0.08)
                        : "transparent",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, "& svg": { width: 20, height: 20 } }}>
                      <GroupIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={group.title}
                      primaryTypographyProps={{
                        fontSize: "0.9rem",
                        fontWeight: hasActiveItem ? 600 : 500,
                      }}
                    />
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List disablePadding>
                    {group.items.map((item) => {
                      const Icon = item.icon;
                      const active = isActive(item.href);
                      const isDisabled = item.disabled || item.comingSoon;

                      return (
                        <ListItem key={item.href} disablePadding>
                          <ListItemButton
                            component={isDisabled ? "div" : Link}
                            to={isDisabled ? undefined : item.href}
                            onClick={isDisabled ? undefined : onClose}
                            disabled={isDisabled}
                            selected={active}
                            sx={{
                              mx: 1,
                              ml: 3,
                              borderRadius: 1,
                              mb: 0.5,
                              opacity: isDisabled ? 0.5 : 1,
                              "&.Mui-selected": {
                                bgcolor: (theme) =>
                                  alpha(theme.palette.primary.main, 0.12),
                              },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, "& svg": { width: 18, height: 18 } }}>
                              <Icon />
                            </ListItemIcon>
                            <ListItemText
                              primary={item.title}
                              primaryTypographyProps={{
                                fontSize: "0.85rem",
                                fontWeight: active ? 600 : 400,
                              }}
                            />
                            {item.badge && (
                              <Chip
                                label={item.badge}
                                size="small"
                                sx={{
                                  height: 18,
                                  fontSize: "0.6rem",
                                  bgcolor: item.comingSoon
                                    ? "warning.light"
                                    : "primary.light",
                                  color: item.comingSoon
                                    ? "warning.dark"
                                    : "primary.dark",
                                }}
                              />
                            )}
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          textAlign: "center",
        }}
      >
        <Typography variant="caption" color="text.secondary">
          © 2024 Nemesis
        </Typography>
      </Box>
    </Box>
  );
}
